import { createFileRoute, redirect } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Grid from "@mui/material/Grid";
import LinearProgress from "@mui/material/LinearProgress";
import Divider from "@mui/material/Divider";
import Alert from "@mui/material/Alert";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import CircularProgress from "@mui/material/CircularProgress";
import { useTheme } from "@mui/material/styles";
import { toast } from "sonner";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import VideocamOffOutlinedIcon from "@mui/icons-material/VideocamOffOutlined";
import StopOutlinedIcon from "@mui/icons-material/StopOutlined";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlineOutlined";
import GraphicEqIcon from "@mui/icons-material/GraphicEq";
import SkipNextIcon from "@mui/icons-material/SkipNext";

import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";

import { PageHeader } from "@/components/PageHeader";
import { QuotaUpgradeDialog } from "@/components/QuotaUpgradeDialog";
import { useQuotaError } from "@/hooks/useQuotaError";
import { CAPABILITIES } from "@/lib/quotas";
import { TrendSparklines } from "@/components/interview/TrendSparklines";
import { SessionDetailDrawer } from "@/components/interview/SessionDetailDrawer";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { transcribeInterview } from "@/server/transcribe-interview";
import {
  analyzeInterview,
  listInterviewSessions,
  deleteInterviewSession,
} from "@/server/analyze-interview";
import {
  generateInterviewQuestions,
  listJDsForInterview,
} from "@/server/generate-interview-questions";

export const Route = createFileRoute("/app/interview")({
  beforeLoad: () => {
    throw redirect({ to: "/app/jobs" });
  },
  component: () => null,
});

type SessionRow = Awaited<ReturnType<typeof listInterviewSessions>>["sessions"][number];
type JDRow = Awaited<ReturnType<typeof listJDsForInterview>>["jds"][number];
type GenQ = { text: string; focus: string; why: string };

const DEFAULT_QUESTIONS: GenQ[] = [
  { text: "Tell me about a challenging project you led recently.", focus: "leadership", why: "General leadership probe." },
  { text: "Describe a time you handled conflict on your team.", focus: "conflict", why: "General teamwork probe." },
  { text: "Walk me through a technical decision you regret.", focus: "decision", why: "Self-awareness probe." },
  { text: "Tell me about a time you missed a deadline. What happened?", focus: "failure", why: "Accountability probe." },
  { text: "Describe a moment you had to influence without authority.", focus: "stakeholder", why: "Influence probe." },
  { text: "Tell me about a time you received tough feedback.", focus: "growth", why: "Coachability probe." },
];

const FILLER_WORDS = [
  "um",
  "uh",
  "like",
  "you know",
  "basically",
  "actually",
  "literally",
  "sort of",
  "kind of",
  "right",
];

const fmtTime = (s: number) =>
  `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(Math.floor(s % 60)).padStart(2, "0")}`;

async function getAuthHeaders() {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new Error("You must be signed in.");
  return { authorization: `Bearer ${token}` };
}

function highlightTranscript(text: string): { html: string } {
  const escaped = text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
  let html = escaped;
  for (const w of FILLER_WORDS) {
    const rx = new RegExp(`\\b(${w.replace(/ /g, "\\s+")})\\b`, "gi");
    html = html.replace(
      rx,
      '<mark style="background: rgba(255,193,7,0.35); border-radius:3px; padding:0 2px;">$1</mark>',
    );
  }
  return { html };
}

function pacingTone(wpm: number): "success" | "warning" | "error" {
  if (wpm >= 130 && wpm <= 160) return "success";
  if (wpm >= 110 && wpm <= 180) return "warning";
  return "error";
}

function pacingHint(wpm: number): string {
  if (wpm < 110) return "Pace is slow — aim for 130–150 wpm to feel confident.";
  if (wpm > 180) return "Pace is fast — slow down so the interviewer can absorb your points.";
  if (wpm >= 130 && wpm <= 160) return "Excellent pacing — confident and measured.";
  return "Pacing is acceptable but not ideal — target 130–150 wpm.";
}

const STAGE_OPTIONS: Array<{ key: string; label: string }> = [
  { key: "", label: "General (any round)" },
  { key: "recruiter", label: "Recruiter" },
  { key: "hiring_manager", label: "Hiring Manager" },
  { key: "portfolio", label: "Portfolio" },
  { key: "panel", label: "Panel" },
  { key: "leadership", label: "Leadership" },
  { key: "offer", label: "Offer" },
];

export function InterviewPanel({
  jdId = null,
  hideHeader = false,
  hideJdPicker = false,
  defaultStageKey = null,
}: {
  jdId?: string | null;
  hideHeader?: boolean;
  hideJdPicker?: boolean;
  defaultStageKey?: string | null;
} = {}) {
  const theme = useTheme();
  const { user } = useAuth();

  // Question pool — JD-aware when generated
  const [questions, setQuestions] = useState<GenQ[]>(DEFAULT_QUESTIONS);
  const [questionIdx, setQuestionIdx] = useState(0);
  const question = questions[questionIdx]?.text ?? DEFAULT_QUESTIONS[0].text;
  const [jds, setJds] = useState<JDRow[]>([]);
  const [selectedJdId, setSelectedJdId] = useState<string>(jdId ?? "");
  const [stageKey, setStageKey] = useState<string>(defaultStageKey ?? "");
  const [genBusy, setGenBusy] = useState(false);
  const questionQuotaDialog = useQuotaError(CAPABILITIES.interview_question);
  const rerecordQuotaDialog = useQuotaError(CAPABILITIES.interview_rerecord);

  // Capture state
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const startedAtRef = useRef<number | null>(null);

  const [hasPermission, setHasPermission] = useState<boolean | null>(null);
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [busy, setBusy] = useState(false);
  const [busyLabel, setBusyLabel] = useState("");

  const [report, setReport] = useState<{
    sessionId: string;
    transcript: string;
    durationSec: number;
    wpm: number;
    fillerCount: number;
    fillerBreakdown: Record<string, number>;
    sentiment: string;
    starScore: number;
    starBreakdown: {
      situation: number;
      situationNote: string;
      task: number;
      taskNote: string;
      action: number;
      actionNote: string;
      result: number;
      resultNote: string;
    };
    feedback: string;
  } | null>(null);

  const [sessions, setSessions] = useState<SessionRow[]>([]);
  const [openSession, setOpenSession] = useState<SessionRow | null>(null);

  // Load history + JDs
  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const headers = await getAuthHeaders();
        const [hist, jdRes] = await Promise.all([
          listInterviewSessions({ headers }),
          listJDsForInterview({ headers }),
        ]);
        setSessions(hist.sessions);
        setJds(jdRes.jds as JDRow[]);
      } catch {
        // ignore
      }
    })();
  }, [user]);

  const handleGenerateQuestions = async () => {
    setGenBusy(true);
    try {
      const headers = await getAuthHeaders();
      const res = await generateInterviewQuestions({
        data: { jdId: selectedJdId || null, stageKey: stageKey || null },
        headers,
      });
      setQuestions(res.questions as GenQ[]);
      setQuestionIdx(0);
      setReport(null);
      toast.success(
        stageKey
          ? `Generated ${stageKey.replace("_", " ")}-round questions.`
          : "Generated tailored questions.",
      );
    } catch (e) {
      if (questionQuotaDialog.handle(e)) return;
      toast.error(e instanceof Error ? e.message : "Could not generate questions.");
    } finally {
      setGenBusy(false);
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      streamRef.current?.getTracks().forEach((t) => t.stop());
    };
  }, []);

  // Timer tick
  useEffect(() => {
    if (!recording) return;
    const id = window.setInterval(() => {
      if (startedAtRef.current) {
        setElapsed((Date.now() - startedAtRef.current) / 1000);
      }
    }, 200);
    return () => window.clearInterval(id);
  }, [recording]);

  const requestCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: 640, height: 480 },
        audio: true,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => undefined);
      }
      setHasPermission(true);
    } catch (e) {
      setHasPermission(false);
      toast.error(
        e instanceof Error
          ? `Camera/mic blocked: ${e.message}`
          : "Camera or microphone permission was denied.",
      );
    }
  };

  const stopCamera = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
    setHasPermission(null);
  };

  const start = async () => {
    if (!streamRef.current) {
      await requestCamera();
      if (!streamRef.current) return;
    }
    setReport(null);
    chunksRef.current = [];
    const mimeCandidates = [
      "audio/webm;codecs=opus",
      "audio/webm",
      "audio/mp4",
    ];
    let mime = "";
    for (const m of mimeCandidates) {
      if (typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported(m)) {
        mime = m;
        break;
      }
    }
    // Audio-only stream for upload (smaller); video stays in preview
    const audioTracks = streamRef.current.getAudioTracks();
    const audioStream = new MediaStream(audioTracks);
    const recorder = new MediaRecorder(audioStream, mime ? { mimeType: mime } : undefined);
    recorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
    };
    recorder.start(1000);
    recorderRef.current = recorder;
    startedAtRef.current = Date.now();
    setElapsed(0);
    setRecording(true);
  };

  const stopAndAnalyze = async () => {
    const recorder = recorderRef.current;
    if (!recorder || !user) return;

    setRecording(false);
    setBusy(true);
    setBusyLabel("Finalising recording…");

    const stopped = new Promise<void>((resolve) => {
      recorder.addEventListener("stop", () => resolve(), { once: true });
    });
    recorder.stop();
    await stopped;

    const durationSec =
      startedAtRef.current ? (Date.now() - startedAtRef.current) / 1000 : elapsed;

    if (durationSec < 2) {
      setBusy(false);
      toast.error("Recording too short — speak for at least a few seconds.");
      return;
    }

    const blob = new Blob(chunksRef.current, { type: recorder.mimeType || "audio/webm" });
    const ext = (recorder.mimeType || "audio/webm").includes("mp4") ? "mp4" : "webm";
    const path = `${user.id}/${Date.now()}.${ext}`;

    try {
      setBusyLabel("Uploading audio…");
      const { error: upErr } = await supabase.storage
        .from("interview-recordings")
        .upload(path, blob, { contentType: blob.type, upsert: false });
      if (upErr) throw upErr;

      setBusyLabel("Transcribing with AI…");
      const headers = await getAuthHeaders();
      const tr = await transcribeInterview({ data: { audioPath: path }, headers });

      if (!tr.transcript) {
        throw new Error("Transcription returned empty — try speaking louder or longer.");
      }

      setBusyLabel("Scoring STAR completeness…");
      const analysis = await analyzeInterview({
        data: {
          question,
          transcript: tr.transcript,
          durationSec,
          audioPath: path,
        },
        headers,
      });

      setReport({
        sessionId: analysis.sessionId,
        transcript: tr.transcript,
        durationSec,
        wpm: analysis.wpm,
        fillerCount: analysis.fillerCount,
        fillerBreakdown: analysis.fillerBreakdown,
        sentiment: analysis.sentiment,
        starScore: analysis.starScore,
        starBreakdown: analysis.starBreakdown as typeof report extends null
          ? never
          : NonNullable<typeof report>["starBreakdown"],
        feedback: analysis.feedback,
      });

      // Refresh history
      const list = await listInterviewSessions({ headers });
      setSessions(list.sessions);
      toast.success("Report card ready.");
    } catch (e) {
      if (rerecordQuotaDialog.handle(e)) return;
      toast.error(e instanceof Error ? e.message : "Analysis failed.");
    } finally {
      setBusy(false);
      setBusyLabel("");
    }
  };

  const removeSession = async (id: string) => {
    try {
      const headers = await getAuthHeaders();
      await deleteInterviewSession({ data: { id }, headers });
      setSessions((s) => s.filter((row) => row.id !== id));
      if (report?.sessionId === id) setReport(null);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not delete.");
    }
  };

  const transcriptHtml = useMemo(
    () => (report ? highlightTranscript(report.transcript) : null),
    [report],
  );

  const wpmTone = report ? pacingTone(report.wpm) : "warning";
  const wpmPct = report ? Math.min(100, Math.round((report.wpm / 200) * 100)) : 0;
  const starPct = report ? Math.round((report.starScore / 5) * 100) : 0;

  const StarBar = ({ label, value, note }: { label: string; value: number; note: string }) => (
    <Box sx={{ mb: 1.5 }}>
      <Stack direction="row" spacing={1} sx={{ alignItems: "center", mb: 0.5 }}>
        <Typography variant="caption" sx={{ width: 80, fontWeight: 600 }}>
          {label}
        </Typography>
        <LinearProgress
          variant="determinate"
          value={(value / 5) * 100}
          color={value >= 4 ? "success" : value >= 2.5 ? "warning" : "error"}
          sx={{ flex: 1, height: 8, borderRadius: 4 }}
        />
        <Typography variant="caption" sx={{ minWidth: 28, textAlign: "right" }}>
          {value.toFixed(1)}
        </Typography>
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ pl: "92px", display: "block" }}>
        {note}
      </Typography>
    </Box>
  );

  return (
    <Box>
      <PageHeader
        title="Interview practice"
        subtitle="Record an answer with your webcam — get a STAR-graded report card."
      />

      <Grid container spacing={3}>
        {/* LEFT: Question + camera + controls */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Stack direction="row" spacing={1} sx={{ mb: 1, alignItems: "center", flexWrap: "wrap" }}>
                <Chip size="small" label={`Question ${questionIdx + 1} / ${questions.length}`} />
                <Chip size="small" variant="outlined" label={questions[questionIdx]?.focus ?? ""} color="primary" />
                <Box sx={{ flex: 1 }} />
                <Tooltip title="Next question">
                  <IconButton
                    size="small"
                    disabled={recording || busy}
                    onClick={() => {
                      setQuestionIdx((i) => (i + 1) % questions.length);
                      setReport(null);
                    }}
                  >
                    <SkipNextIcon />
                  </IconButton>
                </Tooltip>
              </Stack>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mb: 1.5 }}>
                <TextField
                  select
                  size="small"
                  label="Tailor to JD"
                  value={selectedJdId}
                  onChange={(e) => setSelectedJdId(e.target.value)}
                  sx={{ flex: 1, minWidth: 160 }}
                  disabled={recording || busy || genBusy}
                >
                  <MenuItem value="">Most recent JD</MenuItem>
                  {jds.map((j) => (
                    <MenuItem key={j.id} value={j.id}>
                      {j.role_title || "Untitled"}{j.company ? ` · ${j.company}` : ""}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  size="small"
                  label="Round"
                  value={stageKey}
                  onChange={(e) => setStageKey(e.target.value)}
                  sx={{ minWidth: 160 }}
                  disabled={recording || busy || genBusy}
                >
                  {STAGE_OPTIONS.map((opt) => (
                    <MenuItem key={opt.key || "general"} value={opt.key}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </TextField>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={genBusy ? <CircularProgress size={14} /> : <AutoAwesomeIcon />}
                  onClick={handleGenerateQuestions}
                  disabled={recording || busy || genBusy}
                >
                  Generate
                </Button>
              </Stack>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {question}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                Tip: structure your answer using STAR — Situation, Task, Action, Result.
              </Typography>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Box
                sx={{
                  position: "relative",
                  width: "100%",
                  aspectRatio: "4 / 3",
                  bgcolor: "black",
                  borderRadius: 2,
                  overflow: "hidden",
                  mb: 2,
                }}
              >
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
                {!hasPermission && (
                  <Box
                    sx={{
                      position: "absolute",
                      inset: 0,
                      display: "grid",
                      placeItems: "center",
                      color: "white",
                      textAlign: "center",
                      p: 2,
                    }}
                  >
                    <Stack spacing={1} sx={{ alignItems: "center" }}>
                      <VideocamOffOutlinedIcon sx={{ fontSize: 48, opacity: 0.7 }} />
                      <Typography variant="body2" sx={{ opacity: 0.85 }}>
                        Camera and microphone are off
                      </Typography>
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<VideocamOutlinedIcon />}
                        onClick={requestCamera}
                      >
                        Enable camera & mic
                      </Button>
                    </Stack>
                  </Box>
                )}
                {recording && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: 12,
                      left: 12,
                      px: 1.5,
                      py: 0.5,
                      borderRadius: 999,
                      bgcolor: "error.main",
                      color: "error.contrastText",
                      display: "flex",
                      alignItems: "center",
                      gap: 0.75,
                      fontSize: 12,
                      fontWeight: 700,
                      letterSpacing: 0.5,
                    }}
                  >
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        bgcolor: "white",
                        animation: "pulse 1s infinite",
                        "@keyframes pulse": {
                          "0%": { opacity: 1 },
                          "50%": { opacity: 0.3 },
                          "100%": { opacity: 1 },
                        },
                      }}
                    />
                    REC {fmtTime(elapsed)}
                  </Box>
                )}
              </Box>

              <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 1 }}>
                {!recording ? (
                  <Button
                    variant="contained"
                    color="error"
                    startIcon={<GraphicEqIcon />}
                    onClick={start}
                    disabled={busy || hasPermission === false}
                  >
                    Start recording
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<StopOutlinedIcon />}
                    onClick={stopAndAnalyze}
                  >
                    Stop & analyse
                  </Button>
                )}
                {hasPermission && !recording && (
                  <Button
                    variant="outlined"
                    startIcon={<VideocamOffOutlinedIcon />}
                    onClick={stopCamera}
                    disabled={busy}
                  >
                    Turn off camera
                  </Button>
                )}
                {report && !recording && (
                  <Button
                    variant="outlined"
                    startIcon={<RestartAltIcon />}
                    onClick={() => setReport(null)}
                    disabled={busy}
                  >
                    Try again
                  </Button>
                )}
              </Stack>

              {hasPermission === false && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  Browser blocked camera/mic access. Allow access in your browser address bar and reload.
                </Alert>
              )}

              {busy && (
                <Box sx={{ mt: 2 }}>
                  <Stack direction="row" spacing={1} sx={{ alignItems: "center", mb: 0.5 }}>
                    <CircularProgress size={14} />
                    <Typography variant="caption" color="text.secondary">
                      {busyLabel}
                    </Typography>
                  </Stack>
                  <LinearProgress />
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* RIGHT: Report card */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ minHeight: 360 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Report card
              </Typography>
              {!report ? (
                <Typography color="text.secondary" variant="body2">
                  Record an answer to see your STAR score, pacing, and filler-word breakdown.
                </Typography>
              ) : (
                <Stack spacing={2}>
                  {/* Top metrics */}
                  <Grid container spacing={2}>
                    <Grid size={4}>
                      <Box
                        sx={{
                          p: 1.5,
                          borderRadius: 2,
                          bgcolor:
                            theme.palette.mode === "dark"
                              ? "rgba(255,255,255,0.04)"
                              : "rgba(0,0,0,0.03)",
                        }}
                      >
                        <Typography variant="caption" color="text.secondary">
                          STAR score
                        </Typography>
                        <Typography variant="h5" sx={{ fontWeight: 700 }}>
                          {report.starScore.toFixed(1)}
                          <Typography component="span" variant="caption" color="text.secondary">
                            {" "}/ 5
                          </Typography>
                        </Typography>
                        <LinearProgress
                          variant="determinate"
                          value={starPct}
                          color={
                            report.starScore >= 4
                              ? "success"
                              : report.starScore >= 2.5
                                ? "warning"
                                : "error"
                          }
                          sx={{ mt: 0.5, height: 6, borderRadius: 3 }}
                        />
                      </Box>
                    </Grid>
                    <Grid size={4}>
                      <Box
                        sx={{
                          p: 1.5,
                          borderRadius: 2,
                          bgcolor:
                            theme.palette.mode === "dark"
                              ? "rgba(255,255,255,0.04)"
                              : "rgba(0,0,0,0.03)",
                        }}
                      >
                        <Typography variant="caption" color="text.secondary">
                          Pacing
                        </Typography>
                        <Typography variant="h5" sx={{ fontWeight: 700 }}>
                          {report.wpm}
                          <Typography component="span" variant="caption" color="text.secondary">
                            {" "}wpm
                          </Typography>
                        </Typography>
                        <LinearProgress
                          variant="determinate"
                          value={wpmPct}
                          color={wpmTone}
                          sx={{ mt: 0.5, height: 6, borderRadius: 3 }}
                        />
                      </Box>
                    </Grid>
                    <Grid size={4}>
                      <Box
                        sx={{
                          p: 1.5,
                          borderRadius: 2,
                          bgcolor:
                            theme.palette.mode === "dark"
                              ? "rgba(255,255,255,0.04)"
                              : "rgba(0,0,0,0.03)",
                        }}
                      >
                        <Typography variant="caption" color="text.secondary">
                          Fillers
                        </Typography>
                        <Typography variant="h5" sx={{ fontWeight: 700 }}>
                          {report.fillerCount}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {fmtTime(report.durationSec)} · {report.sentiment}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>

                  <Typography variant="caption" color="text.secondary">
                    {pacingHint(report.wpm)}
                  </Typography>

                  <Divider />

                  <Box>
                    <Typography variant="overline" color="text.secondary">
                      STAR breakdown
                    </Typography>
                    <Box sx={{ mt: 1 }}>
                      <StarBar
                        label="Situation"
                        value={report.starBreakdown.situation}
                        note={report.starBreakdown.situationNote}
                      />
                      <StarBar
                        label="Task"
                        value={report.starBreakdown.task}
                        note={report.starBreakdown.taskNote}
                      />
                      <StarBar
                        label="Action"
                        value={report.starBreakdown.action}
                        note={report.starBreakdown.actionNote}
                      />
                      <StarBar
                        label="Result"
                        value={report.starBreakdown.result}
                        note={report.starBreakdown.resultNote}
                      />
                    </Box>
                  </Box>

                  {Object.keys(report.fillerBreakdown).length > 0 && (
                    <Box>
                      <Typography variant="overline" color="text.secondary">
                        Filler words
                      </Typography>
                      <Stack direction="row" useFlexGap sx={{ mt: 0.5, flexWrap: "wrap", gap: 0.5 }}>
                        {Object.entries(report.fillerBreakdown)
                          .sort((a, b) => b[1] - a[1])
                          .map(([w, n]) => (
                            <Chip key={w} size="small" label={`${w} · ${n}`} color="warning" variant="outlined" />
                          ))}
                      </Stack>
                    </Box>
                  )}

                  <Divider />

                  <Box>
                    <Typography variant="overline" color="text.secondary">
                      Coach feedback
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      {report.feedback}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="overline" color="text.secondary">
                      Transcript (fillers highlighted)
                    </Typography>
                    {transcriptHtml && (
                      <Box
                        sx={{
                          mt: 0.5,
                          p: 1.5,
                          maxHeight: 220,
                          overflowY: "auto",
                          borderRadius: 1.5,
                          border: `1px solid ${theme.palette.divider}`,
                          bgcolor:
                            theme.palette.mode === "dark"
                              ? "rgba(255,255,255,0.02)"
                              : "rgba(0,0,0,0.02)",
                          fontSize: 14,
                          lineHeight: 1.55,
                          whiteSpace: "pre-wrap",
                        }}
                        dangerouslySetInnerHTML={{ __html: transcriptHtml.html }}
                      />
                    )}
                  </Box>
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* History */}
        <Grid size={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Recent sessions
              </Typography>
              <TrendSparklines sessions={sessions} />
              {sessions.length === 0 ? (
                <Typography color="text.secondary" variant="body2">
                  Your past practice sessions will appear here.
                </Typography>
              ) : (
                <Stack divider={<Divider />} spacing={0}>
                  {sessions.map((s) => (
                    <Stack
                      key={s.id}
                      direction={{ xs: "column", sm: "row" }}
                      spacing={1}
                      sx={{
                        py: 1.5,
                        alignItems: { sm: "center" },
                      }}
                    >
                      <Box
                        component="button"
                        type="button"
                        onClick={() => setOpenSession(s)}
                        sx={{
                          flex: 1,
                          minWidth: 0,
                          textAlign: "left",
                          background: "none",
                          border: 0,
                          p: 0,
                          color: "inherit",
                          font: "inherit",
                          cursor: "pointer",
                          "&:hover .session-question": {
                            color: "primary.main",
                            textDecoration: "underline",
                          },
                          "&:focus-visible": {
                            outline: `2px solid ${theme.palette.primary.main}`,
                            outlineOffset: 2,
                            borderRadius: 4,
                          },
                        }}
                        aria-label={`View detail for: ${s.question}`}
                      >
                        <Typography
                          className="session-question"
                          variant="body2"
                          sx={{ fontWeight: 600 }}
                          noWrap
                        >
                          {s.question}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(s.created_at).toLocaleString()} · {fmtTime(Number(s.duration_sec))} · tap to view detail
                        </Typography>
                      </Box>
                      <Stack
                        direction="row"
                        spacing={0.5}
                        onClick={() => setOpenSession(s)}
                        sx={{ cursor: "pointer" }}
                      >
                        <Chip size="small" label={`STAR ${Number(s.star_score).toFixed(1)}`} />
                        <Chip size="small" label={`${s.wpm} wpm`} variant="outlined" />
                        <Chip size="small" label={`${s.filler_count} fillers`} variant="outlined" />
                        {s.sentiment && (
                          <Chip size="small" label={s.sentiment} color="info" variant="outlined" />
                        )}
                      </Stack>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeSession(s.id);
                        }}
                        aria-label="Delete session"
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  ))}
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <SessionDetailDrawer
        session={openSession}
        open={openSession !== null}
        onClose={() => setOpenSession(null)}
      />

      <QuotaUpgradeDialog
        open={questionQuotaDialog.open}
        onClose={questionQuotaDialog.close}
        quota={questionQuotaDialog.quota}
        message={questionQuotaDialog.message}
      />
      <QuotaUpgradeDialog
        open={rerecordQuotaDialog.open}
        onClose={rerecordQuotaDialog.close}
        quota={rerecordQuotaDialog.quota}
        message={rerecordQuotaDialog.message}
      />
    </Box>
  );
}
